import {
  EMBED_APP_SOURCE,
  EMBED_EVENT_PARAM_KEYS,
  EMBED_V2_PROTOCOL,
  EMBED_V2_PROTOCOL_VERSION,
  type BookingStep,
  type EmbedEventName,
  type EmbedEventParams,
  type EmbedMessageType,
  type EmbedV2Message,
} from '@embed-protocol';
import { getEmbedParams } from './iframeResize';
import { getEmbedContext } from './embedContext';

/**
 * Business events for the v2 embed script, which re-emits them on the
 * restaurant's site with the site's own GA/pixel. Only v2 frames with a
 * validated parent origin post anything: a raw iframe's parent never asked for
 * these, and a `'*'` target would hand them to whoever embeds us.
 */

const EMITTED_STORE_KEY = 'embed:emitted';
const MAX_REMEMBERED = 50;

function postToEmbedder<T extends EmbedMessageType>(
  type: T,
  payload: EmbedV2Message<T>['payload'],
  win: Window,
): boolean {
  if (!getEmbedContext(win).isV2) return false;
  const { frameId, parentOrigin } = getEmbedParams(win);
  if (!parentOrigin) return false;

  const message: EmbedV2Message<T> = {
    source: EMBED_APP_SOURCE,
    protocol: EMBED_V2_PROTOCOL,
    version: EMBED_V2_PROTOCOL_VERSION,
    frameId,
    type,
    payload,
  };
  try {
    win.parent.postMessage(message, parentOrigin);
    return true;
  } catch {
    return false;
  }
}

function readEmitted(win: Window): string[] {
  try {
    const parsed: unknown = JSON.parse(win.sessionStorage?.getItem(EMITTED_STORE_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : [];
  } catch {
    return [];
  }
}

function rememberEmitted(win: Window, key: string) {
  try {
    const keys = [...readEmitted(win), key].slice(-MAX_REMEMBERED);
    win.sessionStorage?.setItem(EMITTED_STORE_KEY, JSON.stringify(keys));
  } catch {
    // Without storage a reload of the final page may emit once more.
  }
}

function whitelist(params: Record<string, unknown>): EmbedEventParams {
  const clean: EmbedEventParams = {};
  for (const key of EMBED_EVENT_PARAM_KEYS) {
    const value = params[key];
    if ((typeof value === 'string' && value !== '') || (typeof value === 'number' && Number.isFinite(value))) {
      clean[key] = value;
    }
  }
  return clean;
}

function restaurantOf(win: Window): string {
  return win.location.hostname.split('.')[0] ?? '';
}

/**
 * Posts a business event to the embedding site. `dedupeKey` makes a conversion
 * fire once even when its page is reloaded; it is internal and never sent.
 */
export function emitEmbedEvent(
  name: EmbedEventName,
  params: Record<string, unknown> = {},
  dedupeKey?: string,
  win: Window = window,
): void {
  // Callers run right after a charge or a created booking: measurement must
  // never turn their success into an error.
  try {
    const context = getEmbedContext(win);
    if (!context.isV2) return;
    if (dedupeKey && readEmitted(win).includes(dedupeKey)) return;

    const sent = postToEmbedder(
      'event',
      {
        name,
        params: whitelist({ ...params, restaurant: restaurantOf(win), embed_mode: context.mode }),
      },
      win,
    );
    if (sent && dedupeKey) rememberEmitted(win, dedupeKey);
  } catch (error) {
    console.warn('[embed] event not sent:', error);
  }
}

const STEP_BY_PATH: Record<string, BookingStep> = {
  '/select-diners': 'select_diners',
  '/select-date': 'select_date',
  '/full-calendar': 'select_date',
  '/select-service': 'select_service',
  '/select-menu-filter': 'select_menu',
  '/select-area-filter': 'select_area',
  '/select-area': 'select_area',
  '/select-experience': 'select_experience',
  '/select-time': 'select_time',
  '/maridaje': 'pairings',
  '/personalize': 'personalize',
  '/payment': 'payment',
  '/confirm-guarantee': 'payment',
  '/confirmation': 'confirmation',
  '/waiting-list': 'waitlist',
  '/gift-card': 'gift_card',
};

export function stepForPath(pathname: string): BookingStep | null {
  return STEP_BY_PATH[pathname] ?? null;
}

/**
 * ESC inside the frame never reaches the embedding page, so the frame asks the
 * script to close the panel — unless one of our own dialogs is open, which ESC
 * closes first.
 */
export function startEmbedCloseRequests(win: Window = window): () => void {
  if (!getEmbedContext(win).isV2) return () => {};

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return;
    if (win.document.querySelector('[data-embed-overlay]')) return;
    postToEmbedder('close-request', {}, win);
  };
  win.addEventListener('keydown', onKeyDown as EventListener);
  return () => win.removeEventListener('keydown', onKeyDown as EventListener);
}
